import { Alert, Button, Paper, Stack, SxProps, TextField, Theme, Typography } from "@mui/material";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import useApplyErrorToForm from "~/modules/app/hooks/useApplyErrorToForm";

import useLogin from "../../hooks/useLogin";

interface Props {
    sx?: SxProps<Theme>;
    onLoggedIn: () => void;
}

export default function LoginForm({ sx = [], onLoggedIn }: Props) {
    const { t } = useTranslation("auth");
    const { login, loading, error } = useLogin();

    const {
        handleSubmit,
        register,
        setError,
        formState: { errors, isValid },
    } = useForm<{ email: string; password: string }>({
        mode: "onTouched",
        defaultValues: {
            email: "",
            password: "",
        },
    });

    const onSubmit: SubmitHandler<{
        email: string;
        password: string;
    }> = async data => {
        login(data.email, data.password, () => onLoggedIn());
    };

    const navigate = useNavigate();

    useApplyErrorToForm(error, setError);

    return (
        <Paper sx={sx}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Stack padding={2} spacing={3}>
                    <Typography variant="h2" gutterBottom>
                        {t("loginForm.title")}
                    </Typography>

                    {errors.root && (
                        <Alert severity="error">
                            {errors.root.cause &&
                            "statusCode" in errors.root.cause &&
                            errors.root.cause.statusCode === 404 ? (
                                <Typography>{t("loginForm.errorUsernameOrPassword")}</Typography>
                            ) : (
                                <Typography>{errors.root.message}</Typography>
                            )}
                        </Alert>
                    )}

                    <TextField
                        fullWidth
                        label={t("loginForm.emailLabel")}
                        margin="normal"
                        error={!!errors.email}
                        helperText={errors.email?.message}
                        required
                        {...register("email", { required: true })}
                    />

                    <TextField
                        fullWidth
                        label={t("loginForm.passwordLabel")}
                        type="password"
                        margin="normal"
                        error={!!errors.password}
                        helperText={errors.password?.message}
                        required
                        {...register("password", { required: true })}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        fullWidth
                        loading={loading}
                        disabled={!isValid}
                    >
                        {t("loginForm.btnLogin")}
                    </Button>
                    <Button variant="text" onClick={() => navigate("/forgot-password")}>
                        {t("loginForm.btnForgotPassword")}
                    </Button>
                </Stack>
            </form>
        </Paper>
    );
}
